import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  useScout,
  useMyFleet,
  type MapIslandDto,
  type ShipDto,
} from '@/api/hooks'
import { GoldButton } from '@/components/game/ui'
import { ScopeIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import { ShipPickerList } from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

/** Hard cap on the éclaireur fleet (mirrors ScoutInput::MAX_FLEET server-side). */
const SCOUT_FLEET_CAP = 5
/** Recon depth a single Longue-vue (Spyglass) module adds (mirrors ShipModule::reconDepth). */
const SPYGLASS_RECON_DEPTH = 3

/**
 * Send a FLEET of éclaireurs (1–{@link SCOUT_FLEET_CAP} ships) against a foreign
 * PLAYER island — reveals its defending fleet (threat readout) and notifies the
 * target. The recon depth SUMS across the fleet's Longue-vue modules, so more
 * éclaireurs see past a higher Watchtower. The whole fleet sails at the slowest
 * ship's pace. The launch is disabled with no ship selected.
 */
export function ScoutLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const scout = useScout()
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, SCOUT_FLEET_CAP, () =>
        toast.error(`Flotte d'éclaireurs au max (${SCOUT_FLEET_CAP} navires).`),
      ),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, SCOUT_FLEET_CAP, () =>
        toast.error(`Flotte d'éclaireurs au max (${SCOUT_FLEET_CAP} navires).`),
      ),
    )
  }

  const selectedShips = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is ShipDto => s !== undefined)

  // Recon depth = Σ Longue-vue modules across the fleet (one per ship, ×3 each).
  const reconDepth = selectedShips.reduce(
    (acc, s) => acc + s.modules.filter((m) => m.type === 'spyglass').length * SPYGLASS_RECON_DEPTH,
    0,
  )

  const origin = fleet?.island
  const slowestSpeed = convoySpeedPerHour(selectedShips, fleet?.travelSpeedForgeMult ?? 1)
  const tripSeconds = origin && selectedShips.length > 0 ? tripDurationSeconds(slowestSpeed, origin, island) : 0

  if (dockedShips.length === 0) {
    return (
      <div className="bg-ocean-900/45 rounded-lg p-3 border border-sand-500/10">
        <p className="text-xs text-ocean-300 italic">
          Aucun navire à quai pour envoyer un éclaireur.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-ocean-900/45 rounded-lg p-3 space-y-2 border border-sand-500/10">
      <p className="text-xs text-ocean-300 uppercase tracking-wider flex items-center gap-1.5">
        <ScopeIcon className="w-4 h-4 text-sand-300" /> Éclaireurs
      </p>
      <p className="text-[11px] text-ocean-300 leading-snug">
        Une flotte d'éclaireurs (1 à {SCOUT_FLEET_CAP}) révèle les défenses de l'île, et prévient son propriétaire.
        La <strong className="text-sand-200">Longue-vue</strong> de chaque navire ajoute sa portée : plus tu en envoies, plus tu vois loin.
      </p>
      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={SCOUT_FLEET_CAP}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />
      {selectedShips.length > 0 && (
        <>
          <p className="text-xs text-ocean-300 flex items-center justify-between gap-1">
            <span className="inline-flex items-center gap-1"><ScopeIcon className="w-3.5 h-3.5 text-sand-300" /> Reconnaissance</span>
            <span className="font-display tabular-nums text-sand-200">{reconDepth}</span>
          </p>
          <p className="text-xs text-ocean-300 flex items-center justify-between gap-1">
            <span>Trajet</span>
            <TripDuration seconds={tripSeconds} />
          </p>
          <ConvoyPace ships={selectedShips} tripSeconds={tripSeconds} />
        </>
      )}
      <GoldButton
        onClick={() => {
          if (selectedShips.length === 0) return
          const count = selectedShips.length
          // Post only the still-docked ids (a ship may have left the dock while
          // the panel was open) so the payload matches what the UI shows.
          scout.mutate(
            { shipIds: selectedShips.map((s) => s.id), islandId: island.id },
            {
              onSuccess: () => {
                toast.success(`${count} éclaireur${count > 1 ? 's partent' : ' part'} en reconnaissance vers ${island.name}.`)
                setSelectedIds([])
              },
              onError: (err) => toast.error(err.message || 'Reconnaissance impossible'),
            },
          )
        }}
        disabled={scout.isPending || selectedShips.length === 0}
        className="w-full text-sm px-3 py-1.5"
      >
        {scout.isPending ? 'Lancement…' : `Envoyer ${selectedShips.length > 0 ? selectedShips.length + ' ' : ''}éclaireur${selectedShips.length > 1 ? 's' : ''}`}
      </GoldButton>
    </div>
  )
}
